import { useEffect, useRef, useState } from 'react';

import type { PlanningEntityIdentifier, PlanningEntityType } from 'generated';
import { useSelector } from 'hooks';
import { useRecoilValue } from 'recoil';
import dayjs from 'dayjs';

import type { PlanningEntity } from '../../types';

import {
  getItemWidthInPixels,
  getStartingPoint
} from '../../TimelineView/utils';

import { appointmentDropPositionXAtom } from '../../TimelineView/atoms';

import { Box } from '@mui/material';

import { AppointmentChipPragmatic } from '../AppointmentChipPragmatic/AppointmentChipPragmatic';
import ConditionalDraggableChipPragmatic from '../AppointmentChipPragmatic/ConditionalDraggableChipPragmatic';
import { MultiDragOverlay } from '../../TimelineView/MultiDragOverlay';

import TravelTime from '../../TimelineView/TimelineRoute/TravelTime';
import AppointmentContext from '../../TimelineView/AppointmentContext';
import { getEmployeeName } from 'components/roster-portal/activity-planning/utils';
import type { EditAppointmentType } from 'RestAPIs/activities/types';
import { useTranslation } from 'react-i18next';
import type { ZoomLevel } from '../../TimelineView/TimelineBar/TimelineBar.enums';
import { ZoomLevel as DetailZoomLevel } from '../../components/Header/ZoomLevelMenu.enums';
import type { Interval } from '../../TimelineView/types';
import {
  clampToInterval,
  getAbsenceLabelTimes
} from './AppointmentsPragmatic.utils';

type Props = {
  activeEntityContainingSelection: string | null;
  connectAppointmentGrouping: (
    appointmentGroupingEntityId: string,
    editAppointmentType?: EditAppointmentType,
    scheduleStart?: string
  ) => void;
  connectSchedule?: (
    planningEntityIdentifier?: PlanningEntityIdentifier,
    scheduleStart?: string
  ) => void;
  planningEntity: PlanningEntity;
  planningEntityType?: PlanningEntityType;
  onChangeActiveEntity?: (entityId: string) => void;
  onChangeSelection?: (selectionIds: string[]) => void;
  scheduleStart?: string;
  interval: Interval;
  zoomLevel: ZoomLevel;
  timeBarItemWidth: number;
};

// Pragmatic port of `Appointments`; chips use ConditionalDraggableChipPragmatic
// and multi-drag positioning is handled natively by centerUnderPointer.
const AppointmentsPragmatic = ({
  activeEntityContainingSelection,
  connectAppointmentGrouping,
  connectSchedule,
  interval,
  onChangeActiveEntity,
  onChangeSelection,
  planningEntity,
  planningEntityType,
  zoomLevel,
  timeBarItemWidth
}: Props) => {
  'use memo';
  const { planningEntityIdentifier, schedules, absences, availabilities } =
    planningEntity;
  const planningEntityId =
    planningEntityIdentifier?.entityId ??
    planningEntityIdentifier?.externalEntityId ??
    '';

  const { hasActivityPlanningEditAccess } = useSelector(({ grants }) => grants);
  // Lifted from per-chip useSelector: ~5 000 subscriptions → 1 per route.
  const defaultDetailZoomLevel = useSelector(
    (state) => state.preferences.defaultZoomLevel
  );
  const detailZoomLevel = defaultDetailZoomLevel ?? DetailZoomLevel.STANDARD;
  const appointmentDropPositionX = useRecoilValue(appointmentDropPositionXAtom);

  const { t } = useTranslation('common', { keyPrefix: 'activityPlanning' });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Stable identity ref so a number prop doesn't re-render chips.
  const selectionCountRef = useRef(0);
  selectionCountRef.current = selectedIds.length;
  const isActiveEntity: boolean =
    activeEntityContainingSelection === planningEntityId;

  // Latest-value refs so `onHandleSelect` stays referentially stable (chips
  // skip re-render on it) without capturing stale props/callbacks.
  const planningEntityIdRef = useRef(planningEntityId);
  planningEntityIdRef.current = planningEntityId;
  const onChangeActiveEntityRef = useRef(onChangeActiveEntity);
  onChangeActiveEntityRef.current = onChangeActiveEntity;
  const onChangeSelectionRef = useRef(onChangeSelection);
  onChangeSelectionRef.current = onChangeSelection;

  const { start: scheduleStart } = schedules?.[0] || {};
  const events = schedules?.map((s) => s.events).flatMap((e) => e);
  // Drop DROPZONE/FREE up-front (~20% fewer iterations per route).
  const appointments = events?.filter(
    ({ category }) => category !== 'DROPZONE' && category !== 'FREE'
  );

  const onHandleSelect = (id: string) => {
    setSelectedIds((selectedIds) => {
      onChangeActiveEntityRef.current?.(planningEntityIdRef.current);

      if (selectedIds.includes(id))
        return selectedIds.filter((value) => value !== id);
      if (!selectedIds.length) return [id];
      return selectedIds.concat(id);
    });
  };

  useEffect(() => {
    if (!isActiveEntity)
      setSelectedIds((prev) => (prev.length > 0 ? [] : prev));
  }, [isActiveEntity]);

  useEffect(() => {
    onChangeSelectionRef.current?.(selectedIds);
  }, [selectedIds]);

  return (
    <>
      {appointments?.map((props, i: number) => {
        const key = `${i}-${props.id}-${props.start}`;

        const startingPoint = getStartingPoint(
          props.start,
          interval,
          zoomLevel,
          timeBarItemWidth
        );

        const widthInPixels = getItemWidthInPixels(
          timeBarItemWidth,
          props.durationInSeconds,
          zoomLevel
        );

        const appointmentWidthInPixels = Math.max(4, widthInPixels);

        if (!widthInPixels) return null;

        if (props.category === 'TRAVELTIME') {
          return (
            <TravelTime
              key={`${key}-traveltime`}
              width={widthInPixels}
              durationInSeconds={props.durationInSeconds}
              distanceInMeters={props.location?.distanceInMeters}
              startingPoint={startingPoint}
            />
          );
        }

        if (props.category === 'MULTIPLE') {
          return (
            <Box
              key={`${key}-multiple`}
              sx={{
                position: 'absolute',
                top: '50%',
                left: appointmentDropPositionX?.[props.id]
              }}
            >
              <MultiDragOverlay id={props.id} count={props.count} />
            </Box>
          );
        }

        if (!hasActivityPlanningEditAccess || props.isAbsent) {
          return (
            <Box
              key={`${key}-appointmentchip`}
              sx={{
                position: 'absolute',
                top: '50%',
                left: startingPoint,
                zIndex: props.isAbsent ? 1 : 2
              }}
            >
              <AppointmentChipPragmatic
                {...props}
                width={appointmentWidthInPixels}
                detailZoomLevel={detailZoomLevel}
              />
            </Box>
          );
        }

        return (
          <ConditionalDraggableChipPragmatic
            key={`${key}-conditionaldraggablechip`}
            width={appointmentWidthInPixels}
            startingPoint={startingPoint}
            // Resolve here to a primitive so the Recoil map's identity churn
            // doesn't re-render every chip on drop/reset.
            dropPositionX={appointmentDropPositionX?.[props.id]}
            planningEntityId={planningEntityId}
            planningEntityType={planningEntityType}
            selected={selectedIds.includes(props.id)}
            onSelectAppointment={onHandleSelect}
            selectionCountRef={selectionCountRef}
            scheduleStart={scheduleStart}
            isToPlan={false}
            detailZoomLevel={detailZoomLevel}
            {...props}
          />
        );
      })}
      {/* ABSENCES */}
      {absences?.map((props, i: number) => {
        const key = `${i}-${props.start}-absence`;

        const { start, startsDayBefore, endsDayAfter, durationInSeconds } =
          clampToInterval(props.start, props.end, interval);
        const { startString, endString } = getAbsenceLabelTimes(
          props.start,
          props.end,
          startsDayBefore,
          endsDayAfter
        );

        const startingPoint = getStartingPoint(
          start.toISOString(),
          interval,
          zoomLevel,
          timeBarItemWidth
        );
        const widthInPixels = getItemWidthInPixels(
          timeBarItemWidth,
          durationInSeconds,
          zoomLevel
        );

        return (
          <AppointmentContext
            key={key}
            label={`Afwezig van ${startString} tot ${endString}`}
            absence={props}
            type="absence"
            width={widthInPixels}
            startingPoint={startingPoint}
          />
        );
      })}
      {/* AVAILABILITIES */}
      {availabilities?.map((props, i: number) => {
        const key = `${i}-${props.start}-availability`;

        const { start, durationInSeconds } = clampToInterval(
          props.start,
          props.end,
          interval
        );
        const startingPoint = getStartingPoint(
          start.toISOString(),
          interval,
          zoomLevel,
          timeBarItemWidth
        );
        const widthInPixels = getItemWidthInPixels(
          timeBarItemWidth,
          durationInSeconds,
          zoomLevel
        );
        const startTime = dayjs(props.start).format('HH:mm');
        const endTime = dayjs(props.end).format('HH:mm');
        const isUnavailability = props.type === 'NOT_AVAILABLE';

        return (
          <AppointmentContext
            key={key}
            label={
              isUnavailability
                ? t('unavailableFromTilTimeline', {
                    from: startTime,
                    to: endTime
                  })
                : t('availableFromTilTimeline', {
                    from: startTime,
                    to: endTime
                  })
            }
            type={isUnavailability ? 'unavailability' : 'availability'}
            width={widthInPixels}
            startingPoint={startingPoint}
            availability={props}
            connectAppointmentGrouping={connectAppointmentGrouping}
          />
        );
      })}
      {/* SCHEDULES */}
      {schedules?.map((schedule, i: number) => {
        const { employee } = schedule;
        const start = dayjs(schedule.start).format('HH:mm');

        const key = `${i}-${start}-schedule`;

        const startingPoint = getStartingPoint(
          schedule.start,
          interval,
          zoomLevel,
          timeBarItemWidth
        );

        const widthInPixels = getItemWidthInPixels(
          timeBarItemWidth,
          dayjs(schedule.end).diff(schedule.start, 'seconds'),
          zoomLevel
        );

        const isAvailability = schedule.type === 'AVAILABILITY';
        const isOtherSchedule = schedule.type === 'OTHER';

        if (isAvailability) {
          return (
            <AppointmentContext
              key={key}
              label={schedule.name || ''}
              type="availability"
              width={widthInPixels}
              startingPoint={startingPoint}
              schedule={schedule}
              connectAppointmentGrouping={connectAppointmentGrouping}
              connectSchedule={connectSchedule}
              planningEntityIdentifier={planningEntityIdentifier}
            />
          );
        }

        if (isOtherSchedule) {
          return (
            <AppointmentContext
              key={key}
              label={'Geen dienst'}
              type="none"
              width={widthInPixels}
              startingPoint={startingPoint}
              schedule={schedule}
              connectAppointmentGrouping={connectAppointmentGrouping}
              connectSchedule={connectSchedule}
              planningEntityIdentifier={planningEntityIdentifier}
            />
          );
        }

        return (
          <AppointmentContext
            key={key}
            label={
              planningEntityType === 'ROUTE'
                ? getEmployeeName(employee) || 'Geen medewerker'
                : schedule.name || ''
            }
            type="schedule"
            width={widthInPixels}
            startingPoint={startingPoint}
            schedule={schedule}
            connectAppointmentGrouping={connectAppointmentGrouping}
            connectSchedule={connectSchedule}
            planningEntityIdentifier={planningEntityIdentifier}
          />
        );
      })}
    </>
  );
};

export default AppointmentsPragmatic;
